/**
 * Endpoint mutation hook (create/update/delete/import/link).
 */

import { useState, useCallback } from 'react'
import { logger } from '../../utils/logger'
import type {
  CaseEndpoint,
  CreateEndpointRequest,
  CreateNodeRequest,
  UpdateEndpointRequest,
  UpdateNodeRequest,
  UpdatePositionRequest,
  ImportEndpointsRequest,
  EndpointImportResult,
  LinkCollectionRequest,
  LinkEndpointRequest,
} from '../../types/case-container'
import {
  createEndpoint,
  createNode,
  updateEndpoint,
  updateNode,
  updateEndpointPosition,
  linkToCollection,
  linkToEndpoint,
  deleteEndpoint,
  importEndpoints,
} from '../../services/endpoints'

interface UseEndpointMutationsResult {
  creating: boolean
  updating: boolean
  deleting: boolean
  importing: boolean
  linking: boolean
  error: string | null
  create: (caseId: string, input: CreateEndpointRequest) => Promise<CaseEndpoint | null>
  update: (
    caseId: string,
    endpointId: string,
    input: UpdateEndpointRequest
  ) => Promise<CaseEndpoint | null>
  createNode: (caseId: string, input: CreateNodeRequest) => Promise<CaseEndpoint | null>
  updateNode: (
    caseId: string,
    nodeId: string,
    input: UpdateNodeRequest
  ) => Promise<CaseEndpoint | null>
  updatePosition: (
    caseId: string,
    endpointId: string,
    position: UpdatePositionRequest
  ) => Promise<boolean>
  remove: (caseId: string, endpointId: string) => Promise<boolean>
  importBulk: (caseId: string, data: ImportEndpointsRequest) => Promise<EndpointImportResult | null>
  linkCollection: (
    caseId: string,
    nodeId: string,
    request: LinkCollectionRequest
  ) => Promise<boolean>
  linkEndpoint: (caseId: string, nodeId: string, request: LinkEndpointRequest) => Promise<boolean>
  clearError: () => void
}

interface CreateUpdateState {
  setCreating: (val: boolean) => void
  setUpdating: (val: boolean) => void
  setError: (val: string | null) => void
}

type CreateLegacyFn = (caseId: string, input: CreateEndpointRequest) => Promise<CaseEndpoint | null>

function useCreateLegacy(state: CreateUpdateState): CreateLegacyFn {
  return useCallback(
    async (caseId: string, input: CreateEndpointRequest): Promise<CaseEndpoint | null> => {
      state.setCreating(true)
      state.setError(null)
      try {
        return await createEndpoint(caseId, input)
      } catch (err) {
        state.setError(err instanceof Error ? err.message : 'Failed to create endpoint')
        return null
      } finally {
        state.setCreating(false)
      }
    },
    [state]
  )
}

type CreateNodeFn = (caseId: string, input: CreateNodeRequest) => Promise<CaseEndpoint | null>

function useCreateNode(state: CreateUpdateState): CreateNodeFn {
  return useCallback(
    async (caseId: string, input: CreateNodeRequest): Promise<CaseEndpoint | null> => {
      state.setCreating(true)
      state.setError(null)
      try {
        return await createNode(caseId, input)
      } catch (err) {
        state.setError(err instanceof Error ? err.message : 'Failed to create node')
        return null
      } finally {
        state.setCreating(false)
      }
    },
    [state]
  )
}

type UpdateLegacyFn = (
  caseId: string,
  endpointId: string,
  input: UpdateEndpointRequest
) => Promise<CaseEndpoint | null>

function useUpdateLegacy(state: CreateUpdateState): UpdateLegacyFn {
  return useCallback(
    async (
      caseId: string,
      endpointId: string,
      input: UpdateEndpointRequest
    ): Promise<CaseEndpoint | null> => {
      state.setUpdating(true)
      state.setError(null)
      try {
        return await updateEndpoint(caseId, endpointId, input)
      } catch (err) {
        state.setError(err instanceof Error ? err.message : 'Failed to update endpoint')
        return null
      } finally {
        state.setUpdating(false)
      }
    },
    [state]
  )
}

type UpdateNodeFn = (
  caseId: string,
  nodeId: string,
  input: UpdateNodeRequest
) => Promise<CaseEndpoint | null>

function useUpdateNode(state: CreateUpdateState): UpdateNodeFn {
  return useCallback(
    async (
      caseId: string,
      nodeId: string,
      input: UpdateNodeRequest
    ): Promise<CaseEndpoint | null> => {
      state.setUpdating(true)
      state.setError(null)
      try {
        return await updateNode(caseId, nodeId, input)
      } catch (err) {
        state.setError(err instanceof Error ? err.message : 'Failed to update node')
        return null
      } finally {
        state.setUpdating(false)
      }
    },
    [state]
  )
}

type UpdatePositionFn = (
  caseId: string,
  endpointId: string,
  position: UpdatePositionRequest
) => Promise<boolean>

function useUpdatePosition(state: CreateUpdateState): UpdatePositionFn {
  return useCallback(
    async (
      caseId: string,
      endpointId: string,
      position: UpdatePositionRequest
    ): Promise<boolean> => {
      state.setUpdating(true)
      state.setError(null)
      try {
        await updateEndpointPosition(caseId, endpointId, position)
        return true
      } catch (err) {
        state.setError(err instanceof Error ? err.message : 'Failed to update endpoint position')
        return false
      } finally {
        state.setUpdating(false)
      }
    },
    [state]
  )
}

interface DeleteImportState {
  setDeleting: (val: boolean) => void
  setImporting: (val: boolean) => void
  setError: (val: string | null) => void
}

type RemoveFn = (caseId: string, endpointId: string) => Promise<boolean>

function useRemove(state: DeleteImportState): RemoveFn {
  return useCallback(
    async (caseId: string, endpointId: string): Promise<boolean> => {
      state.setDeleting(true)
      state.setError(null)
      try {
        await deleteEndpoint(caseId, endpointId)
        return true
      } catch (err) {
        state.setError(err instanceof Error ? err.message : 'Failed to delete endpoint')
        return false
      } finally {
        state.setDeleting(false)
      }
    },
    [state]
  )
}

type ImportBulkFn = (
  caseId: string,
  data: ImportEndpointsRequest
) => Promise<EndpointImportResult | null>

function useImportBulk(state: DeleteImportState): ImportBulkFn {
  return useCallback(
    async (caseId: string, data: ImportEndpointsRequest): Promise<EndpointImportResult | null> => {
      logger.debug('useEndpointMutations', 'Starting import', {
        caseId,
        count: data.endpoints.length,
      })
      state.setImporting(true)
      state.setError(null)
      try {
        const result = await importEndpoints(caseId, data)
        logger.debug('useEndpointMutations', 'Import succeeded', result)
        return result
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to import endpoints'
        logger.error('useEndpointMutations', 'Import failed', err, { caseId })
        state.setError(errorMsg)
        return null
      } finally {
        state.setImporting(false)
      }
    },
    [state]
  )
}

interface LinkState {
  setLinking: (val: boolean) => void
  setError: (val: string | null) => void
}

type LinkCollectionFn = (
  caseId: string,
  nodeId: string,
  request: LinkCollectionRequest
) => Promise<boolean>

function useLinkCollection(state: LinkState): LinkCollectionFn {
  return useCallback(
    async (caseId: string, nodeId: string, request: LinkCollectionRequest): Promise<boolean> => {
      state.setLinking(true)
      state.setError(null)
      try {
        await linkToCollection(caseId, nodeId, request)
        return true
      } catch (err) {
        state.setError(err instanceof Error ? err.message : 'Failed to link node to collection')
        return false
      } finally {
        state.setLinking(false)
      }
    },
    [state]
  )
}

type LinkEndpointFn = (
  caseId: string,
  nodeId: string,
  request: LinkEndpointRequest
) => Promise<boolean>

function useLinkEndpoint(state: LinkState): LinkEndpointFn {
  return useCallback(
    async (caseId: string, nodeId: string, request: LinkEndpointRequest): Promise<boolean> => {
      state.setLinking(true)
      state.setError(null)
      try {
        await linkToEndpoint(caseId, nodeId, request)
        return true
      } catch (err) {
        state.setError(err instanceof Error ? err.message : 'Failed to link node to endpoint')
        return false
      } finally {
        state.setLinking(false)
      }
    },
    [state]
  )
}

export function useEndpointMutations(): UseEndpointMutationsResult {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [linking, setLinking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cuState: CreateUpdateState = { setCreating, setUpdating, setError }
  const diState: DeleteImportState = { setDeleting, setImporting, setError }
  const linkState: LinkState = { setLinking, setError }

  const create = useCreateLegacy(cuState)
  const createNodeFn = useCreateNode(cuState)
  const update = useUpdateLegacy(cuState)
  const updateNodeFn = useUpdateNode(cuState)
  const updatePositionFn = useUpdatePosition(cuState)
  const remove = useRemove(diState)
  const importBulk = useImportBulk(diState)
  const linkCollectionFn = useLinkCollection(linkState)
  const linkEndpointFn = useLinkEndpoint(linkState)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    creating,
    updating,
    deleting,
    importing,
    linking,
    error,
    create,
    update,
    createNode: createNodeFn,
    updateNode: updateNodeFn,
    updatePosition: updatePositionFn,
    remove,
    importBulk,
    linkCollection: linkCollectionFn,
    linkEndpoint: linkEndpointFn,
    clearError,
  }
}
